<template>
  <SubHeading :home="home" :tabs="tabs" :shorts="false" :currTab="currTab" @active="handleActive" />

  <nav class="sba-collection-breadcrumb enrollee-add-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <router-link to="/enrollee" class="breadcrumb sba-collection-crumb teal-text">
          <span>{{ enrolleeBreadcrumbTitle }}</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text">
          <span>Add New</span>
        </span>
        <span class="sba-collection-context">Manual Monthly Enrollee</span>
      </div>
    </div>
  </nav>

  <div id="top"></div>

  <div class="container enrollee-add-page-shell">
    <div class="enrollee-add-workbench sba-slide-in">
      <div class="enrollee-add-head">
        <div class="enrollee-add-title-block">
          <label class="active teal-text">Backend Service</label>
          <h5>Add Manual Enrollee</h5>
          <p>Create a current-month enrollee record.</p>
        </div>
        <div class="enrollee-period-card">
          <span>Coverage</span>
          <strong>{{ monthCoverageLabel }}</strong>
        </div>
      </div>

      <form class="enrollee-add-form" @submit.prevent="handleSubmit">
        <div class="enrollee-add-section">
          <div class="enrollee-scheme-strip" :class="{ 'has-error': !currentScheme }">
            <i class="material-icons">{{ currentScheme ? "verified_user" : "warning" }}</i>
            <div>
              <span>Scheme</span>
              <strong>{{ currentSchemeName }}</strong>
            </div>
          </div>

          <div class="enrollee-form-grid">
            <label class="enrollee-field">
              <span>Enrollee Name</span>
              <input v-model.trim="form.name" type="text" autocomplete="off" />
            </label>

            <label class="enrollee-field">
              <span>Enrollee ID</span>
              <input v-model.trim="form.enrollee_number" type="text" autocomplete="off" @blur="normalizeId" />
              <small v-if="normalizedPreview && normalizedPreview !== form.enrollee_number">
                Saves as {{ normalizedPreview }}
              </small>
            </label>

            <label class="enrollee-field">
              <span>Sex</span>
              <select v-model="form.sex" class="browser-default">
                <option disabled value="">Select sex</option>
                <option value="m">Male</option>
                <option value="f">Female</option>
              </select>
            </label>

            <label class="enrollee-field">
              <span>Date of Birth</span>
              <input v-model="form.dob" type="date" />
            </label>
          </div>

          <div class="enrollee-type-group">
            <span>Type</span>
            <div class="enrollee-type-options">
              <button
                v-for="option in typeOptions"
                :key="option.value"
                type="button"
                :class="{ active: form.type === option.value }"
                @click="form.type = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div class="enrollee-error" v-if="error">
            <i class="material-icons">error_outline</i>
            <span>{{ error }}</span>
          </div>

          <div class="enrollee-success" v-if="createdEnrollee">
            <i class="material-icons">check_circle</i>
            <div>
              <strong>{{ createdEnrollee.name }}</strong>
              <span>{{ createdEnrollee.enrollee_number }} is active for {{ monthCoverageLabel }}.</span>
            </div>
          </div>
        </div>

        <div class="enrollee-add-actions">
          <router-link to="/enrollee" class="enrollee-back-button">
            <i class="material-icons">arrow_back</i>
            <span>Back</span>
          </router-link>
          <button type="submit" class="enrollee-submit-button" :disabled="!isFormReady">
            <i class="material-icons">{{ isSubmitting ? "hourglass_empty" : "person_add" }}</i>
            <span>{{ submitLabel }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from "vue";
import { useStore } from "vuex";
import useModuleNavigation from "@/composables/useModuleNavigation";
import postItem from "@/composables/postItem";
import { normalizeEnrolleeNumber } from "@/composables/enrolleeNumber";
import SubHeading from "@/components/HeaderSubDiv.vue";
import { ABBR } from "@/laravel/config";
import M from "materialize-css";

export default {
  name: "enrolleeAdd",
  components: { SubHeading },
  setup() {
    const store = useStore();
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const isSubmitting = ref(false);
    const error = ref(null);
    const createdEnrollee = ref(null);
    const { routeToTab } = useModuleNavigation();

    const form = reactive({
      name: "",
      enrollee_number: "",
      sex: "",
      dob: "",
      type: "",
    });

    const typeOptions = [
      { label: "Principal", value: "p" },
      { label: "Spouse", value: "s" },
      { label: "Dependant", value: "d" },
    ];

    const currentScheme = computed(() => {
      const schemes = Array.isArray(store.state.user?.schemes)
        ? store.state.user.schemes
        : [];

      return schemes.find((scheme) => scheme.curr) || schemes[0] || null;
    });

    const currentSchemeName = computed(() => {
      return currentScheme.value?.name || "No active scheme selected";
    });

    const enrolleeBreadcrumbTitle = computed(() => {
      return ABBR ? `${ABBR} Enrollees Home` : "Enrollees Home";
    });

    const normalizedPreview = computed(() => {
      return normalizeEnrolleeNumber(form.enrollee_number);
    });

    const monthCoverageLabel = computed(() => {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const options = { month: "short", day: "numeric", year: "numeric" };

      return `${start.toLocaleDateString(undefined, options)} - ${end.toLocaleDateString(undefined, options)}`;
    });

    const isFormReady = computed(() => {
      return Boolean(
        currentScheme.value &&
        form.name.trim() &&
        normalizedPreview.value &&
        form.sex &&
        form.dob &&
        form.type &&
        !isSubmitting.value,
      );
    });

    const submitLabel = computed(() => {
      return isSubmitting.value ? "Adding..." : "Add Enrollee";
    });

    onMounted(() => {
      document.title = "Add Enrollee";

      tabs.value.push({ id: 1, name: "Enrollees", title: "All", icon: "arrow_back", url: "enrollee", hash: null });
      tabs.value.push({ id: 2, name: "Enrollees", title: "Add New", icon: null, url: null, hash: null });

      currTab.value = tabs.value.find((x) => x.id === 2);
      home.value = tabs.value.find((x) => x.id === 1);
    });

    const handleActive = (tab) => {
      routeToTab(tab, { replace: !tab?.url });
    };

    const normalizeId = () => {
      form.enrollee_number = normalizedPreview.value;
    };

    const resetForm = () => {
      form.name = "";
      form.enrollee_number = "";
      form.sex = "";
      form.dob = "";
      form.type = "";
    };

    const handleSubmit = async () => {
      if (!isFormReady.value) {
        return;
      }

      normalizeId();
      isSubmitting.value = true;
      error.value = null;
      createdEnrollee.value = null;

      const payload = {
        name: form.name.trim(),
        enrollee_number: form.enrollee_number,
        sex: form.sex,
        dob: form.dob,
        type: form.type,
        scheme_id: currentScheme.value.id,
      };

      const { res, err } = await postItem("enrollee/manual", payload);

      isSubmitting.value = false;

      if (res.value?.data) {
        createdEnrollee.value = res.value.data;
        M.toast({ html: "Enrollee added" });
        resetForm();
        return;
      }

      error.value = err.value || "Unable to add enrollee.";
    };

    return {
      home,
      tabs,
      currTab,
      handleActive,
      form,
      typeOptions,
      currentScheme,
      currentSchemeName,
      enrolleeBreadcrumbTitle,
      normalizedPreview,
      monthCoverageLabel,
      isFormReady,
      submitLabel,
      isSubmitting,
      error,
      createdEnrollee,
      normalizeId,
      handleSubmit,
    };
  },
};
</script>

<style scoped>
.enrollee-add-breadcrumb {
  background: #fafafa !important;
  position: relative;
}

.enrollee-add-breadcrumb::after {
  background: #c4d3d9;
  bottom: 0;
  content: "";
  height: 1px;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
}

.enrollee-add-page-shell {
  margin-top: -14px;
  padding-bottom: 36px;
  position: relative;
  z-index: 1;
}

.enrollee-add-workbench {
  background: #fff;
  border: 1px solid #bfd2dc;
  border-radius: 6px;
  box-shadow: 0 10px 24px rgba(38, 50, 56, 0.05);
  color: #263238;
  overflow: hidden;
}

.enrollee-add-head {
  align-items: flex-end;
  border-bottom: 1px solid #d4e1e7;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  padding: 26px 34px;
}

.enrollee-add-title-block label,
.enrollee-period-card span,
.enrollee-scheme-strip span,
.enrollee-field span,
.enrollee-type-group > span {
  display: block;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.enrollee-add-title-block h5 {
  font-size: 1.55rem;
  font-weight: 800;
  margin: 6px 0 3px;
}

.enrollee-add-title-block p {
  color: #607d8b;
  font-size: 0.96rem;
  margin: 0;
}

.enrollee-period-card {
  background: #f7f9fa;
  border: 1px solid #d6e3e9;
  border-radius: 6px;
  min-width: 238px;
  padding: 12px 14px;
}

.enrollee-period-card span {
  color: #607d8b;
  margin-bottom: 6px;
}

.enrollee-period-card strong {
  color: #263238;
}

.enrollee-add-form {
  padding: 26px 34px 34px;
}

.enrollee-add-section {
  border: 1px solid #d4e1e7;
  border-radius: 6px;
  padding: 22px;
}

.enrollee-scheme-strip {
  align-items: center;
  background: #f7f9fa;
  border: 1px solid #d6e3e9;
  display: flex;
  gap: 14px;
  margin-bottom: 24px;
  padding: 12px 14px;
}

.enrollee-scheme-strip.has-error {
  background: #fff6f6;
  border-color: #ffcdd2;
}

.enrollee-scheme-strip i {
  color: #607d8b;
}

.enrollee-scheme-strip.has-error i {
  color: #e53935;
}

.enrollee-scheme-strip span {
  color: #607d8b;
  margin-bottom: 4px;
}

.enrollee-form-grid {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.enrollee-field {
  color: #263238;
  display: block;
}

.enrollee-field span {
  color: #78909c;
  margin-bottom: 6px;
}

.enrollee-field input,
.enrollee-field select {
  border-bottom: 1px solid #90a4ae !important;
  box-shadow: none !important;
  color: #263238;
  font-size: 1rem;
  height: 40px;
  margin: 0;
}

.enrollee-field small {
  color: #607d8b;
  display: block;
  margin-top: 7px;
}

.enrollee-type-group {
  margin-top: 24px;
}

.enrollee-type-group > span {
  color: #78909c;
  margin-bottom: 10px;
}

.enrollee-type-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.enrollee-type-options button {
  background: #fff;
  border: 1px solid #bfd2dc;
  border-radius: 6px;
  color: #263238;
  cursor: pointer;
  font-weight: 800;
  min-height: 40px;
  padding: 0 16px;
}

.enrollee-type-options button.active {
  background: #37474f;
  border-color: #37474f;
  color: #fff;
}

.enrollee-error,
.enrollee-success {
  align-items: flex-start;
  border-radius: 6px;
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding: 14px 16px;
}

.enrollee-error {
  background: #fff6f6;
  border: 1px solid #ffcdd2;
  color: #b71c1c;
}

.enrollee-success {
  background: #eefaf8;
  border: 1px solid #a7dcd5;
  color: #00695c;
}

.enrollee-success strong,
.enrollee-success span {
  display: block;
}

.enrollee-add-actions {
  align-items: center;
  background: #f7f9fa;
  border: 1px solid #d4e1e7;
  border-top: 0;
  display: flex;
  justify-content: space-between;
  padding: 16px 22px;
}

.enrollee-back-button,
.enrollee-submit-button {
  align-items: center;
  border-radius: 6px;
  display: inline-flex;
  font-weight: 800;
  gap: 12px;
  min-height: 46px;
  padding: 0 20px;
  text-transform: uppercase;
}

.enrollee-back-button {
  background: #fff;
  border: 1px solid #bfd2dc;
  color: #263238;
}

.enrollee-submit-button {
  background: #263238;
  border: 1px solid #263238;
  color: #fff;
  cursor: pointer;
}

.enrollee-submit-button:disabled {
  background: #dce5e9;
  border-color: #dce5e9;
  color: #90a4ae;
  cursor: not-allowed;
}

@media (max-width: 760px) {
  .enrollee-add-head,
  .enrollee-add-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .enrollee-add-head,
  .enrollee-add-form {
    padding-left: 20px;
    padding-right: 20px;
  }

  .enrollee-period-card {
    min-width: 0;
  }

  .enrollee-form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
